"use client";

import { Container } from "@mui/material";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";

import { useBookingViewPreference } from "@/hooks/useBookingViewPreference";
import type { StoreData } from "@/server-fns/booking/query";

import { AIView } from "./AIView";
import { ManualView } from "./ManualView";

type Props = { storeId: string; storeData: StoreData };

const variants = {
    enter: (direction: number) => ({
        x: direction > 0 ? 48 : -48,
        opacity: 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (direction: number) => ({
        x: direction > 0 ? -48 : 48,
        opacity: 0,
    }),
};

export const View = ({ storeId, storeData }: Props) => {
    const posthog = usePostHog();
    const { preferredView, savePreference, isLoaded } = useBookingViewPreference();
    const { mode } = useSearch({ from: "/$storeId/$slug" });
    const navigate = useNavigate({ from: "/$storeId/$slug" });

    // 2번: 게스트가 예약 페이지 방문 시 Store Group 식별
    useEffect(() => {
        posthog.group("store", storeId, { name: storeData.name });
    }, [posthog, storeId, storeData.name]);

    const [viewMode, setViewMode] = useState<"AI" | "MANUAL">("AI");
    const [direction, setDirection] = useState(1);
    // Tracks whether an initial localStorage-driven redirect or a user-triggered switch has
    // already happened in this mount. Prevents the localStorage preference from re-redirecting
    // to manual mode when the user presses the browser back button to return to AI mode.
    // Resets on unmount (page leave), so return visitors still get the preference applied.
    const redirectDoneRef = useRef(false);

    // URL mode param is the source of truth. On fresh mount, push to manual if localStorage says so.
    useEffect(() => {
        if (mode === "manual") {
            setViewMode("MANUAL");
        } else if (isLoaded) {
            if (!redirectDoneRef.current && preferredView === "MANUAL") {
                redirectDoneRef.current = true;
                posthog.capture("booking_manual_viewed", { trigger: "initial_preference" });
                void navigate({ search: { mode: "manual" }, replace: false });
            } else {
                if (redirectDoneRef.current) {
                    savePreference("AI");
                    posthog.capture("booking_ai_returned", { trigger: "browser_back" });
                } else {
                    posthog.capture("booking_ai_viewed");
                }
                setViewMode("AI");
            }
        }
    }, [mode, isLoaded, preferredView, navigate, savePreference, posthog]);

    const handleManualMode = (hasInput: boolean) => {
        setDirection(1);
        setViewMode("MANUAL");
        savePreference("MANUAL");
        redirectDoneRef.current = true;
        posthog.capture("booking_manual_viewed", { trigger: "button", has_input: hasInput });
        void navigate({ search: { mode: "manual" }, replace: false });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleBackToAI = () => {
        setDirection(-1);
        setViewMode("AI");
        savePreference("AI");
        posthog.capture("booking_ai_returned", { trigger: "back_button_ui" });
        void navigate({ search: {}, replace: true });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <Container
            maxWidth="lg"
            sx={{ py: { xs: 4, md: 6 }, px: { xs: 1.5, sm: 3 }, overflow: "hidden" }}
        >
            <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                    key={viewMode}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.25, ease: "easeOut" }}
                >
                    {viewMode === "AI" ? (
                        <AIView
                            storeId={storeId}
                            storeData={storeData}
                            onManualMode={handleManualMode}
                        />
                    ) : (
                        <ManualView
                            storeId={storeId}
                            storeData={storeData}
                            bookingIntent={null}
                            onBackToAI={handleBackToAI}
                        />
                    )}
                </motion.div>
            </AnimatePresence>
        </Container>
    );
};
